import { Link } from "react-router-dom";
import { Menu, Search } from "lucide-react";
import { useSearchOverlay } from "@/components/layout/search-overlay-context";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

type Props = {
  searchPlaceholder?: string;
  /**
   * "/app/projects" or "/admin/projects" — enables search. Determines whether
   * the mobile search icon navigates to /app/search or /admin/search and
   * which base path the desktop overlay uses. Omitted for superadmin (not a
   * workspace member — endpoints would 403).
   */
  projectsBasePath?: string;
  /** Mobile-only: opens the nav drawer. app-shell owns the drawer state. */
  onOpenNav?: () => void;
};

export function TopBar({ searchPlaceholder = "Search", projectsBasePath, onOpenNav }: Props) {
  const { workspaceName } = useAuth();
  const { openWith } = useSearchOverlay();
  const displayName = workspaceName || "Workspace";

  // Base path for search — "/app" or "/admin" — derived from projectsBasePath.
  const basePath: "/app" | "/admin" | null = projectsBasePath?.startsWith("/admin/")
    ? "/admin"
    : projectsBasePath?.startsWith("/app/")
    ? "/app"
    : null;
  const searchHref = basePath ? `${basePath}/search` : "#";

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/85 px-3 backdrop-blur md:h-16 md:gap-4 md:px-6">
        {/* --- MOBILE: hamburger + centered name + search icon (navigates to /search) --- */}
        <button
          type="button"
          aria-label="Open navigation"
          onClick={onOpenNav}
          className={cn(
            "grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground md:hidden",
            !onOpenNav && "invisible"
          )}
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
        </button>

        <div className="flex-1 truncate text-center text-sm font-medium text-foreground md:hidden">
          {displayName}
        </div>

        <Link
          to={searchHref}
          aria-label="Search"
          className={cn(
            "grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:text-foreground md:hidden",
            !basePath && "invisible"
          )}
        >
          <Search className="h-5 w-5" strokeWidth={1.75} />
        </Link>

        {/* --- DESKTOP: search PILL that opens the shared SearchOverlay + theme toggle --- */}
        <button
          type="button"
          onClick={() => openWith({ kind: "workspace" })}
          aria-label="Open search"
          disabled={!basePath}
          className={cn(
            "mx-auto hidden h-9 w-full max-w-sm items-center gap-2.5 rounded-full border border-border bg-secondary/50 px-4 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:flex",
            !basePath && "cursor-not-allowed opacity-50"
          )}
        >
          <Search className="h-4 w-4 shrink-0" strokeWidth={2} />
          <span className="flex-1 text-left">{searchPlaceholder}</span>
          <kbd className="hidden shrink-0 rounded border border-border bg-background/70 px-1.5 py-0.5 text-[10px] font-medium lg:inline-block">
            /
          </kbd>
        </button>
      </header>
    </>
  );
}
